import { isFilterActive } from '../model/filterModel';
import { LIST_PAGE_SIZE_DEFAULT, LIST_PAGE_SIZES } from '../model/useListPageController';
import type {
  ListColumnDef,
  ListColumnState,
  ListColumnWidthMode,
  ListFilterFieldDef,
  ListFilterOp,
  ListFilterValue,
} from '../model/types';

/**
 * Codecy FE-owned payloadů centrální persistence (BE je ukládá jako opaque string).
 * Zásada: **tolerantní čtení** — šablony se vyvíjejí, uložená data nesmí ListPage rozbít.
 * Neznámé klíče/pole se tiše zahodí, chybějící se doplní výchozími hodnotami.
 */

const PAYLOAD_VERSION = 1;
const FILTER_OPS: ListFilterOp[] = ['contains', 'eq', 'gte', 'lte'];

type Json = Record<string, unknown>;

function parseJson(json: string | null | undefined): unknown {
  if (!json) {
    return null;
  }
  try {
    return JSON.parse(json) as unknown;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(record: Json, key: string): string | undefined {
  const value = record[key];
  return typeof value === 'string' ? value : undefined;
}

function num(record: Json, key: string): number | undefined {
  const value = record[key];
  return typeof value === 'number' ? value : undefined;
}

function bool(record: Json, key: string): boolean | undefined {
  const value = record[key];
  return typeof value === 'boolean' ? value : undefined;
}

function arr(record: Json, key: string): unknown[] | undefined {
  const value = record[key];
  return Array.isArray(value) ? value : undefined;
}

function rec(record: Json, key: string): Json | undefined {
  const value = record[key];
  return isRecord(value) ? value : undefined;
}

// ── Definice pohledu (ListPageView.Payload) ──────────────────────────────────────────────────

/**
 * Kanonická definice filtrů pohledu — ukládají se jen neprázdné nebo připnuté filtry,
 * vždy BEZ `items` (display data žijí v DisplayPayload).
 */
export function encodeViewPayload(
  filters: ListFilterValue[],
  defs: Map<string, ListFilterFieldDef>
): string {
  const significant = filters
    .filter((f) => isFilterActive(defs.get(f.field), f) || f.pinned)
    .map((f) => ({
      field: f.field,
      op: f.op,
      ids: f.ids,
      text: f.text,
      number: f.number ?? undefined,
      from: f.from,
      to: f.to,
      pinned: f.pinned || undefined,
    }));
  return JSON.stringify({ version: PAYLOAD_VERSION, filters: significant });
}

/** Tolerantní dekódování definice pohledu — neznámá pole a nevalidní tvary se zahodí. */
export function decodeViewPayload(
  json: string | null | undefined,
  defs: Map<string, ListFilterFieldDef>
): ListFilterValue[] {
  const root = parseJson(json);
  if (!isRecord(root)) {
    return [];
  }
  const filters = arr(root, 'filters');
  if (!filters) {
    return [];
  }

  const result: ListFilterValue[] = [];
  for (const raw of filters) {
    if (!isRecord(raw)) {
      continue;
    }
    const field = str(raw, 'field');
    if (!field) {
      continue;
    }
    const def = defs.get(field);
    if (!def) {
      continue; // pole už v šabloně neexistuje
    }
    const value: ListFilterValue = { field };
    const op = str(raw, 'op');
    if (op && FILTER_OPS.includes(op as ListFilterOp)) {
      value.op = op as ListFilterOp;
    }
    const ids = arr(raw, 'ids');
    if (ids) {
      value.ids = ids.filter((id): id is string => typeof id === 'string');
    }
    const text = str(raw, 'text');
    if (text !== undefined) {
      value.text = text;
    }
    const number = num(raw, 'number');
    if (number !== undefined) {
      value.number = number;
    }
    const from = str(raw, 'from');
    if (from !== undefined) {
      value.from = from;
    }
    const to = str(raw, 'to');
    if (to !== undefined) {
      value.to = to;
    }
    if (bool(raw, 'pinned') === true) {
      value.pinned = true;
    }
    if ((def.type === 'enum' || def.type === 'selector') && !value.ids) {
      value.ids = [];
    }
    result.push(value);
  }
  return result;
}

// ── Display data pohledu (ListPageView.DisplayPayload) ──────────────────────────────────────

/** Posbírá *Ref modely selector filtrů → DisplayPayload; null, když žádné nejsou. */
export function encodeDisplayPayload(
  filters: ListFilterValue[],
  defs: Map<string, ListFilterFieldDef>
): string | null {
  const refs: Record<string, unknown[]> = {};
  for (const filter of filters) {
    const def = defs.get(filter.field);
    if (def?.type === 'selector' && filter.items && filter.items.length > 0) {
      refs[filter.field] = filter.items;
    }
  }
  if (Object.keys(refs).length === 0) {
    return null;
  }
  return JSON.stringify({ version: PAYLOAD_VERSION, refs });
}

/** Tolerantní dekódování display dat — vrací mapu `field → Ref[]`. */
export function decodeDisplayPayload(
  json: string | null | undefined
): Record<string, unknown[]> | undefined {
  const root = parseJson(json);
  if (!isRecord(root)) {
    return undefined;
  }
  const rawRefs = rec(root, 'refs');
  if (!rawRefs) {
    return undefined;
  }
  const refs: Record<string, unknown[]> = {};
  for (const [field, value] of Object.entries(rawRefs)) {
    if (Array.isArray(value)) {
      refs[field] = value;
    }
  }
  return refs;
}

/**
 * Merge display refs na filtry pohledu — selector filtr dostane `items` pro id, která
 * v refs najde (dle `getId`); id bez refu zůstávají (chip ukáže id do doběhu revalidace).
 */
export function applyDisplayRefs(
  filters: ListFilterValue[],
  refs: Record<string, unknown[]> | undefined,
  defs: Map<string, ListFilterFieldDef>
): ListFilterValue[] {
  if (!refs) {
    return filters;
  }
  return filters.map((filter) => {
    const def = defs.get(filter.field);
    if (def?.type !== 'selector' || !filter.ids || filter.ids.length === 0) {
      return filter;
    }
    const candidates = refs[filter.field];
    if (!candidates) {
      return filter;
    }
    const wanted = new Set(filter.ids);
    const items = candidates.filter((item) => {
      try {
        return wanted.has(String(def.getId(item)));
      } catch {
        return false; // Ref v zastaralém tvaru — zahodit, revalidace doplní
      }
    });
    return items.length > 0 ? { ...filter, items } : filter;
  });
}

// ── Per-uživatel stav (ListPageUserState.Payload) ────────────────────────────────────────────

export interface ListUserStatePrefs {
  pageSize?: number;
  live?: boolean;
  panelOpen?: boolean;
  panelWidth?: number;
  /** Zobrazený souhrnný pruh statistik. */
  stats?: boolean;
}

export interface DecodedUserState {
  viewOrder?: string[];
  lastActiveViewId?: string;
  columns?: { widthMode: ListColumnWidthMode; items: ListColumnState[] };
  prefs?: ListUserStatePrefs;
}

export function encodeUserState(state: {
  viewOrder: string[];
  lastActiveViewId: string;
  widthMode: ListColumnWidthMode;
  columnStates: ListColumnState[];
  prefs: ListUserStatePrefs;
}): string {
  return JSON.stringify({
    version: PAYLOAD_VERSION,
    viewOrder: state.viewOrder,
    lastActiveViewId: state.lastActiveViewId,
    columns: {
      widthMode: state.widthMode,
      items: state.columnStates.map((c) => ({
        key: c.key,
        visible: c.visible,
        pinned: c.pinned,
        width: c.width,
      })),
    },
    prefs: state.prefs,
  });
}

/**
 * Tolerantní dekódování user-state: sloupce se rekonciliují proti aktuálnímu registru —
 * neznámé klíče se zahodí, sloupce nově přidané do šablony se zařadí na konec s výchozími
 * hodnotami; pageSize mimo povolenou množinu se vrátí na výchozí.
 */
export function decodeUserState<TItem>(
  json: string | null | undefined,
  columnDefs: ListColumnDef<TItem>[]
): DecodedUserState {
  const root = parseJson(json);
  if (!isRecord(root)) {
    return {};
  }

  const result: DecodedUserState = {};

  const viewOrder = arr(root, 'viewOrder');
  if (viewOrder) {
    result.viewOrder = viewOrder.filter((id): id is string => typeof id === 'string');
  }
  const lastActiveViewId = str(root, 'lastActiveViewId');
  if (lastActiveViewId) {
    result.lastActiveViewId = lastActiveViewId;
  }

  const columns = rec(root, 'columns');
  const columnItems = columns ? arr(columns, 'items') : undefined;
  if (columns && columnItems) {
    const byKey = new Map(columnDefs.map((def) => [def.key, def]));
    const items: ListColumnState[] = [];
    for (const raw of columnItems) {
      if (!isRecord(raw)) {
        continue;
      }
      const key = str(raw, 'key');
      if (!key || !byKey.has(key)) {
        continue;
      }
      const width = num(raw, 'width');
      items.push({
        key,
        visible: bool(raw, 'visible') !== false,
        pinned: bool(raw, 'pinned') === true,
        width: width !== undefined && width >= 48 ? Math.round(width) : undefined,
      });
      byKey.delete(key);
    }
    // sloupce nově přidané do šablony — na konec s výchozími hodnotami
    for (const def of byKey.values()) {
      items.push({
        key: def.key,
        visible: def.visible !== false,
        pinned: Boolean(def.frozen),
        width: undefined,
      });
    }
    result.columns = {
      widthMode: str(columns, 'widthMode') === 'manual' ? 'manual' : 'auto',
      items,
    };
  }

  const rawPrefs = rec(root, 'prefs');
  if (rawPrefs) {
    const prefs: ListUserStatePrefs = {};
    const pageSize = num(rawPrefs, 'pageSize');
    if (pageSize !== undefined) {
      prefs.pageSize = (LIST_PAGE_SIZES as readonly number[]).includes(pageSize)
        ? pageSize
        : LIST_PAGE_SIZE_DEFAULT;
    }
    const live = bool(rawPrefs, 'live');
    if (live !== undefined) {
      prefs.live = live;
    }
    const panelOpen = bool(rawPrefs, 'panelOpen');
    if (panelOpen !== undefined) {
      prefs.panelOpen = panelOpen;
    }
    const panelWidth = num(rawPrefs, 'panelWidth');
    if (panelWidth !== undefined) {
      prefs.panelWidth = Math.min(480, Math.max(200, Math.round(panelWidth)));
    }
    const stats = bool(rawPrefs, 'stats');
    if (stats !== undefined) {
      prefs.stats = stats;
    }
    result.prefs = prefs;
  }

  return result;
}
